'use client';

import React, { useState, useEffect } from 'react';
import { IoClose, IoCopyOutline, IoCheckmarkDoneOutline, IoArrowForward } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import InlineOtpVerification from './InlineOtpVerification';
import { trackEvent } from '../lib/posthog';

interface PopupConfig {
  id: number;
  is_active: boolean;
  headline: string;
  subtext: string;
  button_text: string;
  button_url: string;
  bg_image_url?: string;
  bg_image_position?: string;
  coupon_code: string;
  show_guest_otp: boolean;
  guest_discount_text: string;
  delay_seconds: number;
  suppress_hours: number;
}

const LOCAL_STORAGE_KEY = 'astroji_popup_seen_at';

export default function HomepagePopup() {
  const [config, setConfig] = useState<PopupConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [step, setStep] = useState<'phone' | 'otp' | 'revealed'>('phone');
  const [copied, setCopied] = useState(false);
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    let fallbackTimer: NodeJS.Timeout;
    let idleId: number | undefined;
    let hasInteracted = false;
    let schedulePopup: (() => void) | undefined;

    const handleInteraction = () => {
      hasInteracted = true;
      schedulePopup?.();
    };

    window.addEventListener('pointerdown', handleInteraction, { passive: true, once: true });
    window.addEventListener('keydown', handleInteraction, { once: true });

    const fetchConfigAndSchedule = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const isPreview = searchParams.has('preview_popup') || searchParams.has('popup_preview');
        const previewId = searchParams.get('preview_popup_id') || searchParams.get('popup_id');

        const fetchUrl = previewId
          ? `/api/popup-config?id=${encodeURIComponent(previewId)}&t=${Date.now()}`
          : isPreview
            ? `/api/popup-config?t=${Date.now()}`
            : '/api/popup-config';

        const res = await fetch(fetchUrl, {
          priority: 'low',
        } as RequestInit & { priority: 'low' });
        if (!res.ok) return;
        const data: PopupConfig = await res.json();

        if (!data || (!data.is_active && !isPreview)) return;

        if (!isPreview) {
          const seenKey = `astroji_popup_seen_${data.id}`;
          const seenAtStr = localStorage.getItem(seenKey);
          if (seenAtStr) {
            const seenAt = parseInt(seenAtStr, 10);
            const suppressMs = (data.suppress_hours || 24) * 60 * 60 * 1000;
            if (Date.now() - seenAt < suppressMs) {
              return;
            }
          }
        }

        let scheduled = false;
        schedulePopup = () => {
          if (scheduled) return;
          scheduled = true;
          const delayMs = isPreview ? 50 : (data.delay_seconds || 7) * 1000;
          timer = setTimeout(() => {
            setConfig(data);
            setIsVisible(true);
            trackEvent('popup_viewed', { popup_id: data.id });
            // small tick to trigger animation
            setTimeout(() => setIsMounted(true), 20);
          }, delayMs);
        };

        if (isPreview || hasInteracted) schedulePopup();
        fallbackTimer = setTimeout(() => schedulePopup?.(), isPreview ? 500 : 30000);
      } catch (err) {
        console.error('Failed to load homepage popup:', err);
      }
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(fetchConfigAndSchedule, { timeout: 3000 });
    } else {
      timer = setTimeout(fetchConfigAndSchedule, 1500);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  const handleClose = () => {
    trackEvent('popup_closed', { popup_id: config?.id });
    setIsMounted(false);
    setTimeout(() => {
      setIsVisible(false);
      if (config) {
        localStorage.setItem(`astroji_popup_seen_${config.id}`, Date.now().toString());
        localStorage.setItem(LOCAL_STORAGE_KEY, Date.now().toString());
      }
    }, 300);
  };

  const handleCopyCoupon = () => {
    if (!config?.coupon_code) return;
    trackEvent('popup_interacted', { popup_id: config.id, interaction_type: 'coupon_copied' });
    navigator.clipboard.writeText(config.coupon_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = phoneInput.replace(/[^\d]/g, '');
    if (cleanPhone.length >= 10) {
      setPhoneInput(cleanPhone.slice(-10));
      setStep('otp');
    }
  };

  const handleOtpVerified = () => {
    trackEvent('popup_interacted', { popup_id: config?.id, interaction_type: 'otp_verified' });
    setStep('revealed');
  };

  if (!isVisible || !config) return null;

  const redirectTarget = `${config.button_url || '/products'}?COUPON=${encodeURIComponent(config.coupon_code || 'SAVE10')}`;
  const handleCtaClick = () => {
    trackEvent('popup_interacted', {
      popup_id: config.id,
      interaction_type: 'cta_clicked',
      button_text: config.button_text,
      destination_path: redirectTarget,
    });
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
      style={{
        backgroundColor: isMounted ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0)',
        backdropFilter: isMounted ? 'blur(6px)' : 'blur(0px)',
        WebkitBackdropFilter: isMounted ? 'blur(6px)' : 'blur(0px)',
        transition: 'background-color 0.35s ease, backdrop-filter 0.35s ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Modal Shell */}
      <div
        className="relative w-full max-w-[420px] overflow-hidden bg-black text-white sm:rounded-[32px] rounded-t-[32px]"
        style={{
          minHeight: '540px',
          maxHeight: '92dvh',
          boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)',
          transform: isMounted ? 'scale(1) translateY(0)' : 'scale(0.94) translateY(24px)',
          opacity: isMounted ? 1 : 0,
          transition: 'transform 0.38s cubic-bezier(0.34,1.56,0.64,1), opacity 0.28s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── FULL-BLEED BACKGROUND ── */}
        <div className="absolute inset-0">
          {config.bg_image_url ? (
            <img
              src={config.bg_image_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: config.bg_image_position || '50% 30%' }}
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(ellipse at 80% 10%, rgba(251,191,36,0.35) 0%, transparent 55%), linear-gradient(160deg, #1a0c02 0%, #0a0806 60%)',
              }}
            />
          )}
          {/* Layered overlay: strong dark gradient at bottom for readability */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.72) 65%, rgba(0,0,0,0.95) 100%)',
            }}
          />
          {/* Subtle top vignette */}
          <div
            className="absolute inset-x-0 top-0 h-24"
            style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.45), transparent)' }}
          />
        </div>

        {/* ── CLOSE BUTTON ── */}
        <button
          onClick={handleClose}
          aria-label="Close popup"
          className="absolute top-4 right-4 z-30 flex h-9 w-9 items-center justify-center rounded-full text-white/80 hover:text-white transition-all duration-200"
          style={{
            background: 'rgba(0,0,0,0.38)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <IoClose className="h-5 w-5" />
        </button>

        {/* ── BADGE ── */}
        <div className="absolute top-4 left-4 z-30">
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold tracking-widest uppercase"
            style={{
              background: 'rgba(251,191,36,0.18)',
              border: '1px solid rgba(251,191,36,0.4)',
              color: '#fde68a',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 18px rgba(251,191,36,0.25)',
            }}
          >
            {/* Sparkle SVG */}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17 5.8 21.3l2.4-7.4L2 9.4h7.6z" />
            </svg>
            <span>Festive Exclusive</span>
          </div>
        </div>

        {/* ── CONTENT AREA ── */}
        <div
          className="relative z-20 flex flex-col justify-end"
          style={{ minHeight: '540px', padding: '0' }}
        >
          {/* Text block — sits above the glass form */}
          <div className="px-6 pb-5 pt-32">
            <h2
              className="text-[30px] leading-[1.08] font-black text-white"
              style={{ letterSpacing: '-0.03em', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
            >
              {config.headline}
            </h2>
            <p
              className="mt-2.5 text-sm leading-relaxed"
              style={{ color: 'rgba(226,232,240,0.85)' }}
            >
              {config.subtext}
            </p>
          </div>

          {/* Glass form card */}
          <div
            className="mx-3 mb-3 rounded-[22px] overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.13)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            <div className="p-4">
              {/* ── LOGGED IN ── */}
              {isLoggedIn ? (
                <div className="space-y-3">
                  <div
                    className="flex items-center justify-between rounded-xl px-4 py-3"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <div>
                      <span className="block text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#fbbf24' }}>
                        Your exclusive code
                      </span>
                      <span className="block font-mono text-xl font-black mt-0.5" style={{ color: '#fcd34d', letterSpacing: '0.06em' }}>
                        {config.coupon_code || 'SAVE10'}
                      </span>
                    </div>
                    <button
                      onClick={handleCopyCoupon}
                      className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold transition-all duration-200"
                      style={{
                        background: copied ? '#10b981' : '#fbbf24',
                        color: copied ? '#fff' : '#0c0a00',
                      }}
                    >
                      {copied ? (
                        <><IoCheckmarkDoneOutline className="h-4 w-4" /> Copied!</>
                      ) : (
                        <><IoCopyOutline className="h-4 w-4" /> Copy Code</>
                      )}
                    </button>
                  </div>

                  <a
                    href={redirectTarget}
                    onClick={handleCtaClick}
                    className="flex w-full items-center justify-center gap-2 rounded-[14px] py-3.5 text-sm font-extrabold transition-all duration-200 active:scale-[0.97]"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
                      color: '#0c0a00',
                      boxShadow: '0 8px 24px rgba(251,191,36,0.35)',
                    }}
                  >
                    <span>{config.button_text || 'Shop Now'}</span>
                    <IoArrowForward className="h-4 w-4" />
                  </a>
                </div>
              ) : (
                /* ── GUEST ── */
                <div>
                  {!config.show_guest_otp || step === 'revealed' ? (
                    /* Coupon revealed state */
                    <div className="space-y-3">
                      <div
                        className="rounded-xl p-3 text-center"
                        style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)' }}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#6ee7b7' }}>
                          🎉 Coupon Unlocked
                        </p>
                        <div className="mt-1.5 flex items-center justify-center gap-3">
                          <span className="font-mono text-2xl font-black" style={{ color: '#fcd34d', letterSpacing: '0.08em' }}>
                            {config.coupon_code || 'SAVE10'}
                          </span>
                          <button
                            onClick={handleCopyCoupon}
                            className="rounded-lg px-3 py-1.5 text-xs font-bold transition-all"
                            style={{ background: copied ? '#10b981' : '#fbbf24', color: copied ? '#fff' : '#0c0a00' }}
                          >
                            {copied ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      </div>
                      <a
                        href={redirectTarget}
                        onClick={handleCtaClick}
                        className="flex w-full items-center justify-center gap-2 rounded-[14px] py-3.5 text-sm font-extrabold transition-all duration-200 active:scale-[0.97]"
                        style={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
                          color: '#0c0a00',
                          boxShadow: '0 8px 24px rgba(251,191,36,0.35)',
                        }}
                      >
                        <span>{config.button_text || 'Shop Now with Discount'}</span>
                        <IoArrowForward className="h-4 w-4" />
                      </a>
                    </div>
                  ) : step === 'phone' ? (
                    /* Phone entry */
                    <form onSubmit={handlePhoneSubmit} className="space-y-3">
                      <p className="text-center text-xs font-semibold" style={{ color: '#fcd34d' }}>
                        {config.guest_discount_text || 'Enter your number to get 10% OFF — OTP verified!'}
                      </p>

                      {/* Phone input row */}
                      <div className="flex gap-2">
                        <div
                          className="flex h-12 items-center gap-1.5 rounded-[12px] px-3 shrink-0"
                          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                          <span className="text-sm font-bold text-white">🇮🇳</span>
                          <span className="text-sm font-bold text-white/80">+91</span>
                        </div>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={phoneInput}
                          onChange={(e) => setPhoneInput(e.target.value)}
                          placeholder="Enter mobile number"
                          required
                          className="h-12 flex-1 rounded-[12px] px-4 text-sm font-medium text-white placeholder-white/40 focus:outline-none transition-all"
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                          }}
                          onFocus={(e) => {
                            e.target.style.border = '1px solid rgba(251,191,36,0.6)';
                            e.target.style.background = 'rgba(255,255,255,0.12)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '1px solid rgba(255,255,255,0.15)';
                            e.target.style.background = 'rgba(255,255,255,0.08)';
                          }}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={phoneInput.replace(/[^\d]/g, '').length < 10}
                        className="h-12 w-full rounded-[14px] text-sm font-extrabold transition-all duration-200 active:scale-[0.97] disabled:opacity-40"
                        style={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
                          color: '#0c0a00',
                          boxShadow: '0 8px 24px rgba(251,191,36,0.35)',
                        }}
                      >
                        Get My Coupon →
                      </button>
                    </form>
                  ) : (
                    /* OTP verification */
                    <div className="text-white">
                      <InlineOtpVerification
                        phone={phoneInput}
                        onVerified={handleOtpVerified}
                        onCancel={() => setStep('phone')}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Safe area padding for home bar on iOS */}
          <div style={{ height: 'env(safe-area-inset-bottom, 8px)' }} />
        </div>
      </div>
    </div>
  );
}
